import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';

import { abTestBunja } from '@flipster/features/src/shared/ab-test';
import { userIdBunja } from '@flipster/features/src/shared/auth/state';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { getLocalStorageAtom, useLocalStorage } from '@flipster/react/misc/state/use-local-storage';
import { accountBunja } from '@flipster/socket/r230522/account';
import { marginsBunja } from '@flipster/socket/r230522/margin';
import { PendingPosition, ordersBunja } from '@flipster/socket/r230522/order';
import { openPositionsBunja, positionsBunja } from '@flipster/socket/r230522/position';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import Num from '@flipster/utils/num';
import { OrderMarginType } from '@flipster/utils/types/base';
import { OpenPosition } from '@flipster/utils/types/position';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

export const symbolOccupiedPositionBunja = bunja(() => {
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const symbolOccupiedPositionAtom = atom((get) => {
    const pendingPositions = get(pendingPositionsAtom);
    const openPositions = get(openPositionsAtom);

    const combined: Record<string, { Long: string; Short: string }> = {};

    // Combine pending positions
    if (pendingPositions) {
      Object.entries(pendingPositions).forEach(([symbol, positions]) => {
        positions.forEach((position: PendingPosition) => {
          const { side } = position;
          const amount = Num(position.qty || '0').multipliedBy(position.triggerPrice);

          if (!combined[symbol]) {
            combined[symbol] = { Long: '0', Short: '0' };
          }

          combined[symbol][side] = Num(combined[symbol][side]).plus(amount).toString();
        });
      });
    }

    // Combine open positions
    if (openPositions) {
      Object.entries(openPositions).forEach(([symbol, positions]) => {
        positions.forEach((position: OpenPosition) => {
          const { side } = position;
          const p = Num(position.position || '0');
          const amount = side === 'Long' ? p.multipliedBy(position.askPrice) : p.multipliedBy(position.bidPrice);

          if (!combined[symbol]) {
            combined[symbol] = { Long: '0', Short: '0' };
          }

          combined[symbol][side] = Num(combined[symbol][side]).plus(amount).toString();
        });
      });
    }

    return combined;
  });

  return {
    symbolOccupiedPositionAtom,
  };
});
symbolOccupiedPositionBunja.debugLabel = 'symbolOccupiedPositionBunja';

/** one way mode일때는 leverage가 open position symbol의 leverage와 동일하고 lock이 되어야 함. */
export const fixedLeverageBunja = bunja(() => {
  const { symbol } = bunja.use(symbolBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { v2AccountAtom } = bunja.use(accountBunja);
  const { v2OrdersAtom } = bunja.use(ordersBunja);
  const isFixedLeverageAtom = atom((get) => {
    const account = get(v2AccountAtom);
    const orders = get(v2OrdersAtom);
    const openPositions = get(openPositionsAtom);

    if (account?.tradeMode === 'MultiplePositions') return false;

    const openPosition = openPositions?.[symbol];
    // private/positions에 0이 아닌 크기의 심볼 포지션이 존재합니다(항상 슬롯 0에 있어야 함).
    if (openPosition?.some((o) => o.slot === 0 && Num(o.position).abs().isGreaterThan(0))) return true;
    // private/orders에 개설된 주문이 존재합니다.
    if (orders?.[symbol]) return true;
    return false;
  });
  const fixedLeverageAtom = atom((get) => {
    return get(openPositionsAtom)?.[symbol]?.[0].initLeverage || get(pendingPositionsAtom)?.[symbol]?.[0].initLeverage;
  });
  return {
    fixedLeverageAtom,
    isFixedLeverageAtom,
  };
});

export const symbolBunja = bunja(() => {
  const { symbol, symbolInfoAtom: nullableSymbolInfoAtom } = bunja.use(tradeSymbolBunja);
  const symbolInfoAtom = atom((get) => {
    const symbolInfo = get(nullableSymbolInfoAtom);
    if (symbolInfo == null) throw new Error('SymbolInfo cannot be null in perpetual/orderform');
    return symbolInfo;
  });
  return { symbol, symbolInfoAtom };
});

export const perpAccountBunja = bunja(() => {
  const { v2AccountAtom } = bunja.use(accountBunja);
  const { getMarginAtom } = bunja.use(marginsBunja);
  const { marginTypeAtom } = bunja.use(marginTypeBunja);
  const { isFixedLeverageAtom } = bunja.use(fixedLeverageBunja);
  const { symbol } = bunja.use(symbolBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const isOneWayAtom = atom((get) => get(v2AccountAtom)?.tradeMode === 'OneWay');
  const tradeModeAtom = atom((get) => get(v2AccountAtom)?.tradeMode);
  const availableFundsAtom = atom((get) => {
    const margin = get(getMarginAtom);
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    const instrument = instrumentsKeyBySymbol?.[symbol];
    if (!instrument) return '0';

    const marginType = get(marginTypeAtom);
    switch (marginType) {
      case 'Cross':
        return margin[instrument.quoteCurrency]?.crossMarginAvailable || '0';
      case 'Isolated':
        // 1% buffer
        return Num(margin[instrument.quoteCurrency]?.isolatedMarginAvailable || '0')
          .multipliedBy(0.99)
          .toString();
      default:
        // oxlint-disable-next-line no-unused-expressions
        marginType satisfies never;
        // oxlint-disable-next-line no-console
        console.error(`Unexpected marginType: ${marginType}`);
        return '0';
    }
  });
  return { isOneWayAtom, tradeModeAtom, availableFundsAtom, isFixedLeverageAtom };
});

export const marginTypeBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol } = bunja.use(symbolBunja);
  const { getVariant } = bunja.use(abTestBunja);
  const { userIdAtom } = bunja.use(userIdBunja);
  const getMarginTypeStorageAtom = atom((get) =>
    getLocalStorageAtom(`flipster.configs.v1.${get(userIdAtom)}.marginType`, {}),
  );
  const marginTypeAtom = atom((get) => {
    const crossMarginFeatureEnabled = getVariant('tf-cross-margin-and-cross-asset') === 'on';
    if (!crossMarginFeatureEnabled) return 'Isolated';
    const marginTypeStorageAtom = get(getMarginTypeStorageAtom);
    return get(marginTypeStorageAtom)[symbol] ?? 'Isolated';
  });

  const updateMarginType = (mode: OrderMarginType) => {
    const marginTypeStorageAtom = store.get(getMarginTypeStorageAtom);
    store.set(marginTypeStorageAtom, (prev) =>
      prev[symbol] === mode
        ? prev
        : {
            ...prev,
            [symbol]: mode,
          },
    );
  };

  return {
    marginTypeAtom,
    updateMarginType,
  };
});

/**
 * NOTE: When create pending order, Server creates zero position to determine margin mode
 * So if we need to detect the margin mode without open positions, uses zero position's `margin_type`
 */
const useCheckablePositions = (symbol: string | undefined) => {
  const { v2PositionsAtom } = useBunja(positionsBunja);
  const positions = useAtomValue(v2PositionsAtom);
  const { v2OrdersAtom } = useBunja(ordersBunja);
  const orders = useAtomValue(v2OrdersAtom);
  const openPositions = useMemo(() => {
    if (!symbol || !positions) return [];
    return positions.filter((position) => {
      const isNotAirdrop = position.airdropType == null;
      const isSameSymbol = position.symbol === symbol;
      const isEmptyPosition = position.position === '0';
      /**
       * To find anchored pending order
       */
      const isAnchoredPosition = (orders[symbol] ?? []).some((order) => order.slot === position.slot);

      return isNotAirdrop && isSameSymbol && (!isEmptyPosition || isAnchoredPosition);
    });
  }, [positions, symbol, orders]);

  return openPositions;
};

export const useMarginType = () => {
  const { symbol } = useBunja(symbolBunja);
  const { marginTypeAtom, updateMarginType } = useBunja(marginTypeBunja);
  const openPositions = useCheckablePositions(symbol);

  useEffect(() => {
    if (openPositions.length === 0) {
      return;
    }

    updateMarginType(openPositions[0].marginType);
  }, [openPositions, updateMarginType]);

  return { marginTypeAtom, updateMarginType };
};

export const useSymbolMarginType = (symbol: string | undefined): OrderMarginType => {
  const { userIdAtom } = useBunja(userIdBunja);
  const userId = useAtomValue(userIdAtom);
  const [marginType] = useLocalStorage({
    key: `flipster.configs.v1.${userId}.marginType`,
    defaultValue: {},
  });

  const openPositions = useCheckablePositions(symbol);

  if (symbol == null || symbol === '') {
    return 'Isolated';
  }

  if (openPositions.length === 0) {
    return marginType[symbol] || 'Isolated';
  }

  return openPositions[0].marginType;
};
